// opencode-style per-session context inspector. Opens as a right-side
// sheet from a floating button next to the minimap trigger. Shows the
// session's cost/token totals, a breakdown by slot and by model, and the
// context-window usage of the last answering turn. The usage bar is only
// rendered when the last turn's model has a known context window.

import { useQuery } from "@tanstack/react-query";
import type { JSX } from "react";

import { Sheet, SheetBody, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  type SessionContext,
  type UsageStatus,
  getSessionUsage,
  getUsageStatus,
  usageStatusQueryKey,
} from "@/lib/chat";
import { formatTimestamp } from "@/lib/format";
import { SLOT_LABELS, formatBudgetUsd, formatCost, formatTokens } from "@/lib/usage";

interface SessionContextSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string | null;
}

interface SessionContextTriggerProps {
  sessionId: string;
  onClick: () => void;
}

const CONTEXT_RING_RADIUS = 7;
const CONTEXT_RING_CIRCUMFERENCE = 2 * Math.PI * CONTEXT_RING_RADIUS;

export function sessionUsageQueryKey(
  sessionId: string | null,
): readonly ["chat", "session-usage", string | null] {
  return ["chat", "session-usage", sessionId] as const;
}

function formatPct(pct: number): string {
  const value = pct * 100;
  return `${value < 10 ? value.toFixed(1) : Math.round(value)}%`;
}

function formatDateTime(iso: string): string {
  return formatTimestamp(iso);
}

// The daily budget resets at a fixed time of day, so we surface just the
// time-of-day of the next reset instant (rendered in the viewer's local zone).
function formatBudgetReset(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

function slotLabel(slot: string): string {
  return SLOT_LABELS[slot] ?? slot;
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }): JSX.Element {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
      {sub ? <div className="text-xs text-muted-foreground">{sub}</div> : null}
    </div>
  );
}

function SectionTitle({ children }: { children: string }): JSX.Element {
  return <h3 className="text-xs uppercase tracking-wide text-muted-foreground">{children}</h3>;
}

function clampPct(pct: number | null | undefined): number {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return 0;
  return Math.min(1, Math.max(0, pct));
}

export function SessionContextTrigger({
  sessionId,
  onClick,
}: SessionContextTriggerProps): JSX.Element {
  const query = useQuery({
    queryKey: sessionUsageQueryKey(sessionId),
    queryFn: () => getSessionUsage(sessionId),
    enabled: sessionId.length > 0,
    staleTime: 0,
  });
  const usagePct = query.data?.usage.last_turn?.usage_pct ?? null;
  const progress = clampPct(usagePct);
  const dashOffset = CONTEXT_RING_CIRCUMFERENCE * (1 - progress);
  const title =
    usagePct !== null
      ? `Abrir contexto de la sesion (${formatPct(progress)} usado)`
      : "Abrir contexto de la sesion";

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Abrir contexto de la sesion"
      title={title}
      className="rounded-full border border-border bg-card/80 p-1.5 text-muted-foreground shadow-sm backdrop-blur hover:cursor-pointer hover:bg-muted hover:text-foreground"
    >
      <svg
        viewBox="0 0 18 18"
        width="16"
        height="16"
        aria-hidden="true"
        className="block"
        data-testid="session-context-ring"
      >
        <circle
          cx="9"
          cy="9"
          r={CONTEXT_RING_RADIUS}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="opacity-25"
        />
        <circle
          cx="9"
          cy="9"
          r={CONTEXT_RING_RADIUS}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray={CONTEXT_RING_CIRCUMFERENCE}
          strokeDashoffset={dashOffset}
          className="origin-center -rotate-90 text-primary transition-[stroke-dashoffset] duration-300 ease-out"
        />
      </svg>
    </button>
  );
}

export function SessionContextPanel({ ctx }: { ctx: SessionContext }): JSX.Element {
  const { usage } = ctx;
  const lastTurn = usage.last_turn;
  return (
    <div className="space-y-4">
      {lastTurn ? (
        <div className="space-y-1">
          <span className="inline-block rounded-full border border-border px-2 py-0.5 text-xs">
            {lastTurn.provider} · {lastTurn.model_id}
          </span>
          {usage.by_model.length > 1 ? (
            <div className="text-xs text-muted-foreground">
              modelo del último turno · {usage.by_model.length} modelos en la sesión
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-2">
        <Stat label="Costo total" value={formatCost(usage.cost_usd)} sub="de la sesión" />
        <Stat
          label="Tokens"
          value={formatTokens(usage.total_tokens)}
          sub={`in ${formatTokens(usage.prompt_tokens)} · out ${formatTokens(usage.completion_tokens)}`}
        />
        <Stat
          label="Mensajes"
          value={String(ctx.message_count)}
          sub={`${ctx.user_message_count} usuario · ${ctx.assistant_message_count} asistente`}
        />
        <Stat
          label="Latencia media"
          value={
            usage.mean_latency_ms !== null ? `${(usage.mean_latency_ms / 1000).toFixed(1)}s` : "—"
          }
          sub="por turno"
        />
      </div>

      {lastTurn ? (
        <section className="space-y-1">
          <SectionTitle>Ventana del último turno</SectionTitle>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Límite de contexto</span>
            <span>
              {lastTurn.context_window !== null ? formatTokens(lastTurn.context_window) : "—"}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Prompt del último turno</span>
            <span>{formatTokens(lastTurn.context_tokens)}</span>
          </div>
          {lastTurn.usage_pct !== null ? (
            <>
              <div className="flex justify-between text-sm font-medium">
                <span>Uso</span>
                <span>{formatPct(lastTurn.usage_pct)}</span>
              </div>
              {/* biome-ignore lint/a11y/useFocusableInteractive: progressbar is a display-only indicator, not keyboard-focusable */}
              <div
                role="progressbar"
                aria-label="Uso del contexto"
                aria-valuenow={Math.min(100, Math.round(lastTurn.usage_pct * 100))}
                aria-valuemin={0}
                aria-valuemax={100}
                className="h-2 overflow-hidden rounded-full bg-muted"
              >
                <div
                  className="h-full bg-primary"
                  style={{ width: `${Math.min(100, lastTurn.usage_pct * 100)}%` }}
                />
              </div>
            </>
          ) : null}
        </section>
      ) : null}

      {usage.by_slot.length > 0 ? (
        <section className="space-y-1">
          <SectionTitle>Por slot</SectionTitle>
          {usage.by_slot.map((s) => (
            <div key={s.slot} className="flex justify-between text-sm">
              <span>{slotLabel(s.slot)}</span>
              <span className="text-muted-foreground">
                {formatCost(s.cost_usd)} · {formatTokens(s.total_tokens)}
              </span>
            </div>
          ))}
        </section>
      ) : null}

      {usage.by_model.length > 0 ? (
        <section className="space-y-1">
          <SectionTitle>Por modelo</SectionTitle>
          {usage.by_model.map((m) => (
            <div key={`${m.provider}:${m.model_id}`} className="flex justify-between text-sm">
              <span>
                {m.provider} · {m.model_id}
              </span>
              <span className="text-muted-foreground">{formatCost(m.cost_usd)}</span>
            </div>
          ))}
        </section>
      ) : null}

      <section className="space-y-1">
        <SectionTitle>Caché</SectionTitle>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Lectura / escritura</span>
          <span>
            {formatTokens(usage.cache_read_tokens)} / {formatTokens(usage.cache_creation_tokens)}
          </span>
        </div>
      </section>

      <p className="border-t border-border pt-3 text-xs text-muted-foreground">
        Creada {formatDateTime(ctx.started_at)} · última actividad{" "}
        {formatDateTime(ctx.last_activity_at)}
      </p>
    </div>
  );
}

function meterPctFromCost(spent: string, limit: string): number {
  const spentNum = Number.parseFloat(spent);
  const limitNum = Number.parseFloat(limit);
  if (!Number.isFinite(spentNum) || !Number.isFinite(limitNum) || limitNum <= 0) return 0;
  return (spentNum / limitNum) * 100;
}

function Meter({ pct, label }: { pct: number; label: string }): JSX.Element {
  const clamped = Math.min(100, Math.max(0, pct));
  return (
    // biome-ignore lint/a11y/useFocusableInteractive: progressbar is a display-only indicator, not keyboard-focusable
    <div
      role="progressbar"
      aria-label={label}
      aria-valuenow={Math.round(clamped)}
      aria-valuemin={0}
      aria-valuemax={100}
      className="h-2 overflow-hidden rounded-full bg-muted"
    >
      <div className="h-full bg-primary" style={{ width: `${clamped}%` }} />
    </div>
  );
}

// Per-user remaining usage, independent of which session is open: the
// messages-per-minute headroom (always) and the per-user LLM cost budget
// (only when the endpoint reports one). The presentational panel is split out
// so it can be tested without a query, mirroring SessionContextPanel.
export function UsageRemainingPanel({ status }: { status: UsageStatus }): JSX.Element {
  const { chat_send } = status.rate_limits;
  const budget = status.llm_cost;
  const messagesUsedPct =
    chat_send.remaining !== null && chat_send.limit > 0
      ? ((chat_send.limit - chat_send.remaining) / chat_send.limit) * 100
      : 0;
  return (
    <section className="space-y-1">
      <SectionTitle>Tu uso</SectionTitle>
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">Mensajes este minuto</span>
        <span>
          {chat_send.remaining !== null ? `${chat_send.remaining} de ${chat_send.limit}` : "—"}
        </span>
      </div>
      {chat_send.remaining !== null ? (
        <Meter pct={messagesUsedPct} label="Mensajes restantes este minuto" />
      ) : null}
      {budget !== null ? (
        <>
          <div className="flex items-baseline justify-between gap-2 text-sm">
            <span className="text-muted-foreground">Presupuesto diario</span>
            <span className="shrink-0 tabular-nums">
              {formatBudgetUsd(budget.spent_usd)} de {formatBudgetUsd(budget.limit_usd)}
            </span>
          </div>
          <Meter
            pct={meterPctFromCost(budget.spent_usd, budget.limit_usd)}
            label="Presupuesto de costo usado"
          />
          <p className="text-xs text-muted-foreground">
            Se reinicia a las {formatBudgetReset(budget.resets_at)}
          </p>
        </>
      ) : null}
    </section>
  );
}

function UsageRemainingSection({ open }: { open: boolean }): JSX.Element | null {
  const query = useQuery({
    queryKey: usageStatusQueryKey(),
    queryFn: getUsageStatus,
    enabled: open,
    staleTime: 0,
  });
  // Supplementary section: render nothing until data arrives (the backend
  // already fails open, so a missing remaining/budget is encoded in the data).
  if (!query.data) return null;
  return <UsageRemainingPanel status={query.data} />;
}

// Session context body: per-user remaining usage + per-session usage panel.
// Shared by SessionContextSheet (< xl lightbox) and the RightDock Contexto tab
// (>= xl). `enabled` gates the queries so they only fire when the content is
// actually shown (sheet open, or dock tab active).
export function SessionContextContent({
  sessionId,
  enabled = true,
}: {
  sessionId: string | null;
  enabled?: boolean;
}): JSX.Element {
  const query = useQuery({
    queryKey: sessionUsageQueryKey(sessionId),
    queryFn: () => getSessionUsage(sessionId ?? ""),
    enabled: enabled && sessionId !== null,
    // Override the app-wide 30s staleTime: the panel is shown on demand
    // after turns complete, so each open must reflect current session usage
    // rather than a cached snapshot from a previous turn.
    staleTime: 0,
  });

  return (
    <div className="space-y-6">
      <UsageRemainingSection open={enabled} />
      {query.isError ? (
        <p className="text-sm text-destructive">No se pudo cargar el contexto.</p>
      ) : query.data ? (
        <SessionContextPanel ctx={query.data} />
      ) : (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      )}
    </div>
  );
}

export function SessionContextSheet({
  open,
  onOpenChange,
  sessionId,
}: SessionContextSheetProps): JSX.Element {
  return (
    <Sheet open={open} onOpenChange={onOpenChange} ariaLabel="Contexto de la sesión">
      <SheetContent side="right" onClose={() => onOpenChange(false)} className="w-full sm:max-w-sm">
        <SheetHeader>
          <SheetTitle>Contexto</SheetTitle>
        </SheetHeader>
        <SheetBody>
          <SessionContextContent sessionId={sessionId} enabled={open} />
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}
